import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ItemStandardCostHistory {
  costHistoryID: number;
  itemID: number;
  standardCost: number;
  validFrom: string;
  validTo: string;
  createdAt: string;
  userIDEntered: number;
}

export interface ItemStandardCostHistoryCreateRequest {
  itemID: number;
  standardCost: number;
  validFrom: string;
  validTo: string;
  userIDEntered: number;
}

@Injectable({
  providedIn: 'root'
})
export class ItemStandardCostHistoryService {
  private readonly baseUrl = `${environment.apiUrl}/api/ItemStandardCostHistory`;

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  /**
   * Get ItemStandardCostHistory by ID
   */
  getById(costHistoryID: number): Observable<ItemStandardCostHistory> {
    return this.http.get<ItemStandardCostHistory>(
      `${this.baseUrl}/GetById?costHistoryID=${costHistoryID}`,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error fetching ItemStandardCostHistory:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get ItemStandardCostHistory by ItemID (latest record)
   */
  getByItemId(itemID: number): Observable<ItemStandardCostHistory[]> {
    return this.http.get<ItemStandardCostHistory[]>(
      `${this.baseUrl}/GetByItemId?itemID=${itemID}`,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error fetching ItemStandardCostHistory by ItemID:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the latest standard cost for a specific item
   */
  getLatestStandardCost(itemID: number): Observable<ItemStandardCostHistory | null> {
    return this.http.get<ItemStandardCostHistory | null>(
      `${this.baseUrl}/GetLatest?itemID=${itemID}`,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        // If no record exists, return null instead of throwing error
        if (error.status === 404) {
          return [null];
        }
        console.error('Error fetching latest ItemStandardCostHistory:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Add new ItemStandardCostHistory record
   */
  add(costHistory: ItemStandardCostHistoryCreateRequest): Observable<ItemStandardCostHistory> {
    return this.http.post<ItemStandardCostHistory>(
      `${this.baseUrl}/Add`,
      costHistory,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error adding ItemStandardCostHistory:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update existing ItemStandardCostHistory record
   */
  update(costHistoryID: number, costHistory: ItemStandardCostHistoryCreateRequest): Observable<ItemStandardCostHistory> {
    return this.http.put<ItemStandardCostHistory>(
      `${this.baseUrl}/Update?costHistoryID=${costHistoryID}`,
      costHistory,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error updating ItemStandardCostHistory:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete ItemStandardCostHistory record
   */
  delete(costHistoryID: number): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/Delete?costHistoryID=${costHistoryID}`,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Error deleting ItemStandardCostHistory:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if a BOM cost should be saved based on existing standard cost
   * Returns true if the BOM cost should be saved, false otherwise
   */
  shouldSaveBomCost(itemID: number, bomCost: number): Observable<boolean> {
    return new Observable(observer => {
      this.getLatestStandardCost(itemID).subscribe({
        next: (latestCost) => {
          if (!latestCost) {
            // No existing record, so we can save the BOM cost
            observer.next(true);
          } else {
            // Always save when the BOM cost has changed (e.g., after UOM corrections)
            observer.next(Math.abs(latestCost.standardCost - bomCost) > 0.001);
          }
          observer.complete();
        },
        error: (error) => {
          console.error('Error checking existing standard cost:', error);
          // In case of error, allow saving to be safe
          observer.next(true);
          observer.complete();
        }
      });
    });
  }
}
